import { mergeWith, isNumber, isPlainObject } from "lodash"
import { ATTRIBUTION_METHODS } from "helpers/risk"
import { getGroupByQueryParameters, getGroupBySubqueryParameters, getGroupByFields, sanitizePositionSet, hasIdioWeightingRule } from "../helpers"
import { recursiveParseContributors } from "./helpers/contributor-utils"
import { getRiskTotal, mapFactorsAndCategories } from "./helpers/risk-utils"

function sumNestedContributors(contributor1, contributor2) {
  return mergeWith({}, contributor1, contributor2, (value1, value2) => {
    if (isNumber(value1) && isNumber(value2)) {
      return value1 + value2
    }
    if (isPlainObject(value1) && isPlainObject(value2)) {
      return sumNestedContributors(value1, value2)
    }
    return value2 !== undefined ? value2 : value1
  })
}

const ATTRIBUTION_SUBQUERY = `
attribution {
  summary {
    factors
    specific
  }
  factors {
    id
    name
    category
    value
  }
}`

const CONTRIBUTORS_SUBQUERY = `
id
description
modelProviderId
assetClass
notHeld
totalContribution: total
${ATTRIBUTION_SUBQUERY}
`

function getUngroupedContributorsSubQuery(shouldUseIntradayQuery) {
  return `
    riskContributors${shouldUseIntradayQuery ? "(skipCompositeExpansion: $skipCompositeExpansion)" : "(includeNotHeld: $includeNotHeld, attributionMethod: $attributionMethod, skipCompositeExpansion: $skipCompositeExpansion)"}{
      ${CONTRIBUTORS_SUBQUERY}
      ${ATTRIBUTION_SUBQUERY}
    }
  `
}

function getGroupedContributorsSubQuery(groupByFields, shouldUseIntradayQuery) {
  return `
  groupedRiskContributors(${getGroupBySubqueryParameters(groupByFields)}${shouldUseIntradayQuery ? ", skipCompositeExpansion: $skipCompositeExpansion" : ", includeNotHeld: $includeNotHeld, attributionMethod: $attributionMethod, skipCompositeExpansion: $skipCompositeExpansion"}) {
    id
    name
    ${ATTRIBUTION_SUBQUERY}
    contributors{
      ${CONTRIBUTORS_SUBQUERY}
      ${ATTRIBUTION_SUBQUERY}
    }
  }`
}

function getQuery(groupByFields, shouldUseIntradayQuery) {
  const extraParameters = groupByFields ? getGroupByQueryParameters(groupByFields) : ""

  const innerQuery = `
  risk(skipCompositeExpansion: $skipCompositeExpansion) {
    totalPredictedRisk: total
    ${ATTRIBUTION_SUBQUERY}
  }
  ${groupByFields ? getGroupedContributorsSubQuery(groupByFields, shouldUseIntradayQuery) : getUngroupedContributorsSubQuery(shouldUseIntradayQuery)}
  `

  if (shouldUseIntradayQuery) {
    return `
    query($modelId: String!, $positionSet: PositionSetInput!, $on: Date!, $batchId: Id, $skipCompositeExpansion: Boolean${extraParameters}) {
      model(id: $modelId) {
        simulation:intradaySimulation(positionSet: $positionSet, on: $on, batchId: $batchId) {
          ${innerQuery}
        } 
      }
    }`
  }

  return `
  query($modelId: String!, $positionSet: PositionSetInput!, $base: [PositionSetInput], $date: Date!, $attributionMethod: RiskContributorAttributionMethod!, $includeNotHeld: Boolean!, $skipCompositeExpansion: Boolean${extraParameters}) {
    model(id: $modelId) {
      simulation(positionSet: $positionSet, base: $base, from: $date, to: $date) {
        ${innerQuery}
      }
    }
  }`
}

export function createRiskContributorsQuery({ prefix, modelId, positionSet, base, to, groupByOptions, ...options }) {
  const groupByFields = getGroupByFields(groupByOptions)
  const { groupBy, groupById, tier } = groupByFields
  const attributionMethod = options.attributionMethod || ATTRIBUTION_METHODS.MCAR
  const sanitizedPositionSet = sanitizePositionSet(positionSet)
  const skipCompositeExpansion = hasIdioWeightingRule(positionSet)
  const { batchId, on, shouldUseIntradayQuery } = options

  return {
    prefix,
    key: ["risk", "contributors", prefix, modelId, positionSet, base, to, groupBy, groupById, tier, shouldUseIntradayQuery, batchId, on, skipCompositeExpansion],
    query: getQuery(groupBy && groupByFields, shouldUseIntradayQuery),
    variables: {
      modelId,
      positionSet: sanitizedPositionSet,
      batchId: batchId,
      on,
      base,
      date: to,
      ...groupByFields,
      attributionMethod,
      includeNotHeld: !!base,
      skipCompositeExpansion,
    },
    select: (result) => {
      const contributors = groupBy
        ? result.data.model.simulation.groupedRiskContributors
        : result.data.model.simulation.riskContributors

      return {
        label: "Total",
        values: {
          [prefix]: getRisk(result.data.model.simulation.risk[0], true),
        },
        children: recursiveParseContributors(prefix, contributors, options, getRisk, sumNestedContributors),
      }
    },
  }
}

function getRisk(risk, isAttribution) {
  const { summary, factors } = risk?.attribution ?? {}
  let totalContribution = risk?.totalContribution
  if (isAttribution) {
    // At the total level, with the risk attribution query, we do not get the total contribution from the query
    totalContribution = getRiskTotal(risk)
  }

  return {
    ...(totalContribution && { totalContribution }),
    ...(risk?.totalPredictedRisk && { totalPredictedRisk: risk.totalPredictedRisk }),
    ...summary,
    ...(factors && mapFactorsAndCategories(factors)),
  }
}
